import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useUmami } from '@/hooks/useUmami';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import htfcBadge from '@/assests/htfc-badge-2-hi-res-trans.png';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  const { trackEvent } = useUmami();

  // Ensure dark mode preference is applied even on unauthenticated routes
  useEffect(() => {
    const root = window.document.documentElement;
    let isDark = false;
    try {
      const saved = localStorage.getItem('theme');
      if (saved) {
        isDark = saved === 'dark';
      } else if (window.matchMedia) {
        isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
    } catch {}
    if (isDark) root.classList.add('dark'); else root.classList.remove('dark');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    try {
      const user = await login(email, password);
      trackEvent('login', { role: user.role });
      navigate(user.role === 'player' ? '/player' : '/dashboard');
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img src={htfcBadge} alt="Hednesford Town Football Club badge" className="w-12 h-12 rounded" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100">
          Hednesford Data Hub
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Sign in to your account
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-danger-50 border border-danger-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-danger-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-danger-800">
                      {error}
                    </h3>
                  </div>
                </div>
              </div>
            )}

            <Input
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />

            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              endAdornment={
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  tabIndex={0}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M3.53 2.47a.75.75 0 10-1.06 1.06l2.12 2.12A11.26 11.26 0 001.74 12c1.5 4.56 6.04 8.25 10.76 8.25 2.07 0 4.1-.64 5.85-1.8l2.12 2.12a.75.75 0 101.06-1.06l-18-18zM12.5 7.56l1.45 1.45a3.75 3.75 0 013.54 3.54l1.45 1.45a9.73 9.73 0 01-2.11 2.11l-1.15-1.15a3.75 3.75 0 01-5.29-5.29L10.39 9.67c.62-.52 1.36-.9 2.11-1.11z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M12 4.5c-4.72 0-9.26 3.69-10.76 8.25 1.5 4.56 6.04 8.25 10.76 8.25s9.26-3.69 10.76-8.25C21.26 8.19 16.72 4.5 12 4.5zm0 12.75a4.5 4.5 0 110-9 4.5 4.5 0 010 9z" />
                    </svg>
                  )}
                </button>
              }
            />

            <Button
              type="submit"
              loading={isLoading}
              loadingLabel="Signing in"
              className="w-full"
              disabled={!email || !password}
            >
              Sign in
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
